import type { DocViewState } from '../../streamlain/services/doc-view-state';
import type { WorkbenchView } from '../../workbench';

type FullscreenQueryPatch = Partial<{
  fullscreenAttachmentId: string;
  fullscreenDatabaseId: string;
  fullscreenViewId: string;
}>;

const RESTORE_SUPPRESSING_QUERY_KEYS = [
  'mode',
  'blockIds',
  'elementIds',
  'databaseId',
  'databaseRowId',
  'refreshKey',
  'commentId',
  'activityRunId',
  'activityLabel',
  'fullscreenDatabaseId',
  'fullscreenDatabaseRowId',
  'fullscreenDatabaseRowShardDocId',
  'fullscreenDatabaseColumnId',
  'fullscreenAttachmentId',
  'fullscreenAttachmentPageIndex',
  'fullscreenAttachmentQuery',
  'fullscreenAttachmentHighlightRects',
  'fullscreenAttachmentRequestId',
  'fullscreenViewId',
  'fullscreenWorkflowId',
] as const;

function hasQueryValue(value: unknown): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  return true;
}

function hasExplicitNavigationIntent(rawParams: Record<string, unknown>) {
  return RESTORE_SUPPRESSING_QUERY_KEYS.some(key =>
    hasQueryValue(rawParams[key])
  );
}

export function isWorkbenchPathForDoc(pathname: string, docId: string) {
  return pathname === `/${docId}`;
}

export function createSavedFullscreenQueryPatch(
  rawParams: Record<string, unknown>,
  savedState: Pick<DocViewState, 'fullscreen'> | null | undefined
): FullscreenQueryPatch | null {
  const fullscreen = savedState?.fullscreen;
  if (!fullscreen || hasExplicitNavigationIntent(rawParams)) {
    return null;
  }

  if (fullscreen.viewId === 'database') {
    return {
      fullscreenDatabaseId: fullscreen.blockId,
      fullscreenViewId: fullscreen.databaseViewId,
    };
  }

  if (fullscreen.viewId === 'attachment') {
    return {
      fullscreenAttachmentId: fullscreen.blockId,
    };
  }

  return null;
}

export function mergeSavedFullscreenParams<
  TParams extends Record<string, unknown>,
>(
  rawParams: TParams,
  savedState: Pick<DocViewState, 'fullscreen'> | null | undefined
): TParams & FullscreenQueryPatch {
  const patch = createSavedFullscreenQueryPatch(rawParams, savedState);
  return patch ? { ...rawParams, ...patch } : rawParams;
}

/** Late editor effects must never write document parameters into another route. */
export function updateDocQueryString(
  view: {
    history: { location: { pathname: string } };
    updateQueryString: WorkbenchView['updateQueryString'];
  } | null,
  docId: string,
  ...args: Parameters<WorkbenchView['updateQueryString']>
) {
  if (!view || !isWorkbenchPathForDoc(view.history.location.pathname, docId)) {
    return;
  }
  view.updateQueryString(...args);
}
